import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Shield, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useTranslation } from '@/i18n/TranslationContext';
import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { ThemeToggle } from '@/components/layout/ThemeToggle';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();

  // [Phase 3(c) Stage 1, §8] Show an explicit "session expired" notice when the
  // user was bounced here by an expired/invalidated session (flag set by the
  // api interceptor or the inactivity timeout), instead of a silent redirect.
  useEffect(() => {
    if (sessionStorage.getItem('vrs_session_expired')) {
      setSessionExpired(true);
      sessionStorage.removeItem('vrs_session_expired');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError('');
    setSessionExpired(false);
    // [Phase 3(c) Stage 1, §8] Fresh login submit clears any stale stored auth
    // state (and the session-expired flag) so a new sign-in starts clean.
    localStorage.removeItem('vrs_token');
    localStorage.removeItem('vrs_user');
    localStorage.removeItem('vrs_pending_user');
    sessionStorage.removeItem('vrs_session_expired');

    try {
      const response = await authAPI.login(email.trim().toLowerCase(), password);
      const { token, user } = response.data;

      // Check if registration is incomplete
      if (user.registration_status === 'verified_pending_role') {
        // Store token temporarily for role selection
        localStorage.setItem('vrs_token', token);
        localStorage.setItem('vrs_pending_user', JSON.stringify(user));
        navigate('/register?step=role');
        return;
      }

      login(token, user);
      toast.success(t('sign_in'), { description: t('enter_credentials') });

      // Force navigation to dashboard
      window.location.href = '/dashboard';
    } catch (err) {
      const message = err.response?.data?.detail || t('sign_in');
      setError(message);
      toast.error(t('sign_in'), { description: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background vrs-grid relative flex">
      {/* Language toggle — fixed top right */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
        <LanguageToggle />
        <ThemeToggle />
      </div>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(600px circle at 70% 30%, rgba(26,209,230,0.08), transparent 55%), radial-gradient(520px circle at 30% 70%, rgba(0,194,168,0.05), transparent 50%)',
        }}
      />

      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12 xl:px-20 relative">
        <div className="max-w-lg space-y-8">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
              <Shield className="h-7 w-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">VRS</h1>
              <p className="text-xs text-muted-foreground font-medium">{t('app_subtitle')}</p>
            </div>
          </div>
          <div className="space-y-2">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground leading-tight">
              {t('landing_hero')}{' '}
              <span className="text-primary">{t('landing_hero_accent')}</span>
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              {t('landing_desc')}
            </p>
          </div>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="flex flex-1 items-center justify-center p-6 lg:w-1/2">
        <Card className="w-full max-w-[420px] border-border/60 bg-card/80 backdrop-blur-sm shadow-[var(--vrs-shadow-elev-2)]">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-2 lg:hidden mb-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <span className="text-lg font-bold tracking-tight text-foreground">VRS</span>
            </div>
            <CardTitle className="text-xl font-semibold tracking-tight">{t('sign_in')}</CardTitle>
            <CardDescription>{t('enter_credentials')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {/* [Phase 3(c) Stage 1, §8] Explicit session-expired notice */}
              {sessionExpired && (
                <div
                  data-testid="session-expired-banner"
                  className="rounded-lg border border-vrs-amber/30 bg-vrs-amber/10 px-3 py-2 text-sm text-vrs-amber"
                >
                  Your session expired — please sign in again.
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="login-email">{t('email_address')}</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  data-testid="login-email-input"
                  className="h-11 bg-muted/30 border-border focus-visible:ring-2 focus-visible:ring-ring"
                  autoFocus
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="login-password">{t('password')}</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary hover:text-primary/80 transition-colors duration-200"
                    data-testid="login-forgot-password-link"
                  >
                    {t('forgot_password')}
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    data-testid="login-password-input"
                    className="h-11 bg-muted/30 border-border focus-visible:ring-2 focus-visible:ring-ring pr-10"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <p data-testid="login-error-text" className="text-sm text-destructive">{error}</p>
              )}

              <Button
                type="submit"
                data-testid="login-submit-button"
                className="w-full h-11 font-semibold active:scale-[0.98] transition-[background-color,border-color,transform] duration-200"
                disabled={loading}
              >
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('sign_in')}...</> : t('sign_in')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('dont_have_account')}{' '}
                <Link
                  to="/register"
                  className="text-primary font-medium hover:text-primary/80 transition-colors duration-200"
                  data-testid="login-register-link"
                >
                  {t('register_here')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
